import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Loader2, Save, X, Star, ArrowLeft, BadgeCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { SUBURBS } from "@/data/suburbs";

const EMPTY = { author: "", suburb: SUBURBS[0].name, rating: 5, text: "", source: "Google", verified: true, status: "published", order: 0 };
const input = "w-full bg-[#0f0f11] border border-white/15 focus:border-[#d4c4b7] text-stone-100 placeholder:text-stone-600 px-3 py-2.5 text-sm outline-none transition-colors";
const label = "block text-[0.65rem] uppercase tracking-[0.18em] text-stone-500 mb-2";

export default function AdminReviews() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (ready && !user) navigate("/admin/login"); }, [ready, user, navigate]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/reviews"); setItems(data); }
    catch { toast.error("Could not load reviews."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const save = async () => {
    if (!editing.author.trim() || !editing.text.trim()) { toast.error("Author and review text are required."); return; }
    setSaving(true);
    try {
      const payload = { ...editing, rating: Number(editing.rating) || 5, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/reviews/${editing.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success("Review saved."); setEditing(null); load();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try { await api.delete(`/admin/reviews/${id}`); setItems((l) => l.filter((x) => x.id !== id)); toast.success("Deleted."); }
    catch { toast.error("Delete failed."); }
  };

  if (!ready || !user) return <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#d4c4b7]" /></div>;

  return (
    <div className="min-h-screen bg-[#0a0a0c]" data-testid="admin-reviews">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto max-w-[1200px] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/admin" className="inline-flex items-center gap-2 text-stone-400 hover:text-white text-xs uppercase tracking-[0.2em]"><ArrowLeft className="h-3.5 w-3.5" /> Dashboard</a>
            <span className="hidden sm:block overline text-stone-500">Reviews</span>
          </div>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-300 hover:text-[#d4c4b7] px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors"><LogOut className="h-3.5 w-3.5" /> Logout</button>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">
          <h1 className="font-serif text-4xl text-stone-50 font-light">Reviews</h1>
          <button data-testid="review-new-btn" onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-[#9c7a4d] text-white hover:bg-[#b08d5a] px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors"><Plus className="h-4 w-4" /> New Review</button>
        </div>
        <p className="text-stone-500 text-sm mb-6 max-w-2xl">Only <strong className="text-stone-300">Published</strong> reviews appear on the site and in review structured data. Mark a review Verified only if it is a genuine review you can substantiate.</p>

        {loading ? <Loader2 className="h-6 w-6 animate-spin text-[#d4c4b7]" /> : (
          <div className="space-y-3">
            {items.map((r) => (
              <div key={r.id} data-testid={`review-row-${r.id}`} className="border border-white/10 bg-[#0f0f11] p-4">
                <div className="flex items-center gap-2 mb-2">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "text-[#d4c4b7] fill-[#d4c4b7]" : "text-white/15"}`} />)}
                  <span className={`ml-2 text-[0.6rem] uppercase tracking-[0.15em] px-2 py-0.5 border ${r.status === "published" ? "border-emerald-400/40 text-emerald-300" : "border-stone-500/40 text-stone-400"}`}>{r.status}</span>
                  {r.verified ? <span className="text-[0.6rem] uppercase tracking-[0.15em] text-[#d4c4b7] inline-flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Verified</span> : null}
                </div>
                <p className="text-stone-200 text-sm leading-relaxed">“{r.text}”</p>
                <p className="text-stone-500 text-xs mt-2">{r.author} · {r.suburb} · {r.source}</p>
                <div className="flex gap-3 mt-3">
                  <button data-testid={`review-edit-${r.id}`} onClick={() => setEditing({ ...EMPTY, ...r })} className="text-xs uppercase tracking-[0.18em] text-stone-300 hover:text-[#d4c4b7]">Edit</button>
                  <button onClick={() => remove(r.id)} className="text-xs uppercase tracking-[0.18em] text-red-300/70 hover:text-red-300 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
                </div>
              </div>
            ))}
            {items.length === 0 ? <p className="text-stone-500">No reviews yet.</p> : null}
          </div>
        )}
      </div>

      {editing ? (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-lenis-prevent onClick={() => setEditing(null)}>
          <div className="bg-[#0f0f11] border border-white/10 w-full max-w-xl my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="review-editor">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-stone-50">{editing.id ? "Edit review" : "New review"}</h2>
              <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={label}>Author</label><input data-testid="review-author" className={input} value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} /></div>
              <div><label className={label}>Suburb</label><select className={input} value={editing.suburb} onChange={(e) => setEditing({ ...editing, suburb: e.target.value })}>{SUBURBS.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}</select></div>
              <div className="sm:col-span-2"><label className={label}>Review text</label><textarea data-testid="review-text" rows={4} className={`${input} resize-none`} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} /></div>
              <div><label className={label}>Rating</label><select className={input} value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })}>{[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n === 1 ? "" : "s"}</option>)}</select></div>
              <div><label className={label}>Source</label><input className={input} value={editing.source} onChange={(e) => setEditing({ ...editing, source: e.target.value })} placeholder="Google, Website…" /></div>
              <div><label className={label}>Status</label><select data-testid="review-status" className={input} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}><option value="published">Published</option><option value="draft">Draft</option></select></div>
              <div><label className={label}>Order</label><input type="number" className={input} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
              <label className="sm:col-span-2 flex items-center gap-3 text-stone-300 text-sm cursor-pointer"><input type="checkbox" checked={editing.verified} onChange={(e) => setEditing({ ...editing, verified: e.target.checked })} /> Verified (genuine review)</label>
            </div>
            <div className="flex gap-3 mt-6">
              <button data-testid="review-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#9c7a4d] text-white hover:bg-[#b08d5a] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.2em]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
              <button onClick={() => setEditing(null)} className="border border-white/15 text-stone-300 hover:text-white px-6 py-3 text-xs uppercase tracking-[0.2em]">Cancel</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
